import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./social.css";
import Location from "../../../../../assets/icons/location.png";
import { upcomingEvents, pastEvents } from "./socialsData.js";
import Arrow from "../../../../../assets/icons/arrow.gif";
import Social from "../../../../../assets/Socials/social1.jpg";
import SocialBookSlot from "../../../../Modal/BookSlot/SocialBookSlot/SocialBookSlot.jsx";

// ─── Image Zoom Modal ────────────────────────────────────────────────────────
const ImageZoomModal = ({ src, alt, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        zIndex: 99999, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 20,
      }}
      onClick={onClose}
    >
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw", maxHeight: "88vh",
          borderRadius: 12, objectFit: "contain",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        }}
      />
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 20, right: 20,
          background: "rgba(255,255,255,0.15)", border: "none",
          borderRadius: "50%", width: 36, height: 36,
          color: "#fff", fontSize: 20, cursor: "pointer",
        }}
      >×</button>
    </div>
  );
};

// ─── Upcoming Card — Horizontal Split Layout ──────────────────────────────────
const UpcomingCard = ({ event, onRsvpClick }) => {
  const [zoomSrc, setZoomSrc] = useState(null);
  const imgSrc = event.banner;
  const locationLink = event.locationLink || event.locationlink;

  return (
    <>
      <div className="upcoming-split-card">

        {/* LEFT — Image */}
        <div
          className="upcoming-split-image"
          onClick={() => imgSrc && setZoomSrc(imgSrc)}
        >
          {imgSrc ? (
            <img src={imgSrc} alt={event.title} />
          ) : (
            <div className="upcoming-split-no-image">📷</div>
          )}
          <div className="upcoming-split-img-overlay" />
          <span className="upcoming-split-zoom-hint">🔍 Click to zoom</span>
        </div>

        {/* RIGHT — Content */}
        <div className="upcoming-split-content">
          <span className="upcoming-split-date">{event.date}</span>
          <h3 className="upcoming-split-title">{event.title}</h3>
          {locationLink ? (
            <a
              className="upcoming-split-location upcoming-split-location-link"
              href={locationLink}
              target="_blank"
              rel="noreferrer"
            >
              <img src={Location} alt="Location" /> {event.location}
            </a>
          ) : (
            <span className="upcoming-split-location">📍 {event.location}</span>
          )}
          <p className="upcoming-split-desc">{event.shortDescription}</p>

          {event.highlights && event.highlights.length > 0 && (
            <ul className="upcoming-split-highlights">
              {event.highlights.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          )}

          {/* Register Button — at bottom of content */}
          <div className="upcoming-split-footer">
            <button
              className="upcoming-split-register-btn"
              onClick={() => onRsvpClick && onRsvpClick(event)}
            >
              Register Interest
            </button>
          </div>
        </div>
      </div>

      {zoomSrc && (
        <ImageZoomModal src={zoomSrc} alt={event.title} onClose={() => setZoomSrc(null)} />
      )}
    </>
  );
};

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-");

// ─── Main Socials Component ───────────────────────────────────────────────────
export default function Socials() {
  const location = useLocation();
  const { city } = useParams();
  const upcomingImages = [Social];

  const upcomingWithImages = upcomingEvents.map((event, index) => ({
    ...event,
    banner: upcomingImages[index] ?? null,
  }));

  const [activeTab, setActiveTab] = useState("upcoming");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const totalSlides = pastEvents.length;

  useEffect(() => {
    if (isVideoPlaying || activeTab !== "past") return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [totalSlides, isVideoPlaying, activeTab]);

useEffect(() => {
  if (!location.pathname.includes("/events/social/rsvp/") || !city) return;

  const match = upcomingWithImages.find(
    (event) => event.slug === city
  );

  if (match) {
    setSelectedEvent(match);
    setIsBookModalOpen(true);
  }
}, [city, location.pathname, upcomingWithImages]);
  const handlePrev = () =>
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  const handleNext = () =>
    setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));

  return (
    <div className="social-page page-content">

      {/* ── ABOUT SOCIALS ── */}
      <section className="social-section">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="text-center">About IHN Socials</h1>
        </motion.div>
        <p className="text-center">
          IHN Socials is a curated series of thoughtfully designed gatherings that bring together
          members of the Indian Hospitality Network in relaxed, inspiring and culturally rich
          settings. These socials are not just events—they are experiences created to foster genuine
          connections, meaningful conversations and a sense of belonging within the hospitality,
          travel, events and allied industries. Each IHN Social is carefully conceptualised to
          balance learning, leisure and networking, allowing members to engage beyond formal
          business interactions.
        </p>
        <p className="text-center">
          From heritage venues and cultural landmarks to contemporary spaces, IHN Socials celebrate
          destinations that have a story to tell. The intent is to create shared moments where
          professionals can unwind, exchange ideas, explore collaborations and build long-term
          relationships in an informal environment.
        </p>
        <p className="text-center">
          Through IHN Socials, the network continues to strengthen its ethos of connection,
          collaboration and shared growth—one memorable experience at a time.
        </p>
      </section>

      {/* ── TABS ── */}
      <div className="socials-tabs-wrapper">
        <div className="socials-tabs">
          <button
            className={`social-tab-btn ${activeTab === "upcoming" ? "active" : ""}`}
            onClick={() => setActiveTab("upcoming")}
          >
            🗓 Upcoming
          </button>
          <button
            className={`social-tab-btn ${activeTab === "past" ? "active" : ""}`}
            onClick={() => { setActiveTab("past"); setCurrentSlide(0); }}
          >
            🎞 Past Events
          </button>
        </div>
      </div>

      {/* ── TAB CONTENT ── */}
      <AnimatePresence mode="wait">

        {/* ══════ UPCOMING TAB ══════ */}
        {activeTab === "upcoming" && (
          <motion.div
            key="upcoming"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.45 }}
          >
            <section className="social-section light-bg">
              <h2 className="text-center">Upcoming Socials</h2>
              <p className="text-center" style={{ marginBottom: "40px" }}>
                Mark your calendars — here's what we have planned next for our community.
              </p>

              {upcomingWithImages.length > 0 ? (
                <div className="upcoming-split-list">
                  {upcomingWithImages.map((event) => (
                    <UpcomingCard
                      key={event.id}
                      event={event}
                      onRsvpClick={(ev) => {
                        setSelectedEvent(ev);
                        setIsBookModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="no-events-placeholder">
                  <span className="no-events-icon">📅</span>
                  <h3>More Socials Coming Soon</h3>
                  <p>Stay tuned — curated experiences are on the way!</p>
                </div>
              )}
            </section>
          </motion.div>
        )}

        {/* ══════ PAST TAB ══════ */}
        {activeTab === "past" && (
          <motion.div
            key="past"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.45 }}
          >
            <section className="social-section light-bg">
              <h2 className="text-center">Past Socials</h2>

              <div className="social-carousel">
                {/* LEFT ARROW */}
                <button className="testimonial-carousel-btn left" onClick={handlePrev}>
                  <img src={Arrow} alt="Prev" className="nav-arrow-icon" />
                </button>

                {/* SLIDE */}
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 60 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                  className="event-layout"
                >
                  <div className="event-card">
                    <h3>{pastEvents[currentSlide]?.title}</h3>
                    <p>{pastEvents[currentSlide]?.description}</p>

                    {pastEvents[currentSlide]?.points?.length > 0 && (
                      <ul className="event-points">
                        {pastEvents[currentSlide].points.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>
                    )}

                    {pastEvents[currentSlide]?.location && (
                      <p style={{ marginTop: "16px", color: "#861b68", fontWeight: 600 }}>
                        📍 {pastEvents[currentSlide].location}
                      </p>
                    )}
                    {pastEvents[currentSlide]?.date && (
                      <p style={{ color: "#6b7280", fontSize: "14px", marginTop: "4px" }}>
                        🗓 {pastEvents[currentSlide].date}
                      </p>
                    )}
                  </div>

                  <div
                    className="event-video"
                    onMouseEnter={() => setIsVideoPlaying(true)}
                    onMouseLeave={() => setIsVideoPlaying(false)}
                    onClick={() => setIsVideoPlaying(true)}
                  >
                    <iframe
                      src={pastEvents[currentSlide]?.video}
                      title={pastEvents[currentSlide]?.title || "Social Video"}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </motion.div>

                {/* RIGHT ARROW */}
                <button className="testimonial-carousel-btn right" onClick={handleNext}>
                  <img src={Arrow} alt="Next" className="nav-arrow-icon" />
                </button>
              </div>

              {/* Slide Dots */}
              <div className="social-dots">
                {pastEvents.map((_, i) => (
                  <span
                    key={i}
                    className={`social-dot ${i === currentSlide ? "active" : ""}`}
                    onClick={() => setCurrentSlide(i)}
                  />
                ))}
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CTA ── */}
      <section className="social-cta">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="cta-box"
        >
          <h2>More Socials Coming Soon</h2>
          <p>
            Many more curated experiences are on the way — each designed to connect, engage,
            and inspire our growing community.
          </p>
        </motion.div>
      </section>

      {/* ── BOOK SLOT MODAL ── */}
      <SocialBookSlot
        isOpen={isBookModalOpen}
        onClose={() => setIsBookModalOpen(false)}
        eventData={selectedEvent}
      />
    </div>
  );
}
